/**
 * Team → SPOCs (prototype `spList()` / `spDetail()`): each SPOC with the clients they hold, so you can see
 * who is carrying what; open one to see their clients and change a client's SPOC with a reason.
 *
 * Data: GET /clients (every page) grouped by the current SPOC (prototype `SPOCS`: people who hold a client),
 * GET /users for status (Act as), and GET /tasks (open + done, every page) per assignee for the prototype's
 * `spStats`: open, overdue, dates slipped (open tasks moved at least once), days lost (due − first due), closed.
 */
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { SLIP_REASON_LABEL, type ClientRow, type Task, type UserRef } from '@amplispot/contracts';
import { useSession } from '../../lib/session';
import { useClients, useUsers } from '../../lib/data';
import { dayLabel, firstName, rupees, todayIso } from '../../lib/format';
import { daysBetween, dueText, useAllTasks } from '../../lib/work';
import { SlipChip } from '../work/TaskPanel';
import { Avatar, Empty, ErrorState, Loading, PriorityChip, Seg, bandInfo } from '../../ui/kit';
import { Icon } from '../../ui/Icon';
import { ChangeSpocModal, type SpocTarget } from '../ChangeSpocModal';
import { ActAsActions } from '../ActAsModal';
import { Lbl } from '../../shell/Help';

interface SpocRow {
  user: UserRef;
  /** From GET /users; unknown (and so never offered "Act as") for a SPOC the caller cannot list. */
  status?: 'active' | 'disabled';
  clients: ClientRow[];
  revenue: number | null;
  open: number;
  overdue: number;
  slipped: number;
  pushed3: number;
  daysLost: number;
  closed: number;
}

/** Prototype `slipDays`: how far the due date has moved from the first one. */
const slipDays = (t: Task) => (t.orig_due ? Math.max(0, daysBetween(t.orig_due, t.due)) : 0);

const PRIO_ORDER = { high: 0, med: 1, low: 2 } as const;

export function TeamSpocs() {
  const { can } = useSession();
  const clients = useClients();
  const users = useUsers(can('users.read'));
  const readsTasks = can('tasks.read.all') || can('tasks.read.own');
  const openTasks = useAllTasks({ status: 'open' }, readsTasks);
  const doneTasks = useAllTasks({ status: 'done' }, readsTasks);
  const today = todayIso();
  const [params, setParams] = useSearchParams();
  const who = params.get('spoc');

  const rows = useMemo<SpocRow[]>(() => {
    // Only people who hold a client are SPOCs (prototype `SPOCS`); GET /users only adds their status.
    const status = new Map((users.data ?? []).map((u) => [u.id, u.status] as const));
    const by = new Map<string, SpocRow>();
    for (const c of clients.data ?? []) {
      const r = by.get(c.spoc.id) ?? { user: c.spoc, status: status.get(c.spoc.id), clients: [], revenue: null, open: 0, overdue: 0, slipped: 0, pushed3: 0, daysLost: 0, closed: 0 };
      r.clients.push(c);
      if (c.annual_revenue_lakh !== undefined) r.revenue = (r.revenue ?? 0) + (c.annual_revenue_lakh ?? 0);
      by.set(c.spoc.id, r);
    }
    for (const t of openTasks.data ?? []) {
      const r = t.assignee ? by.get(t.assignee.id) : undefined;
      if (!r) continue;
      r.open++;
      if (t.due < today) r.overdue++;
      if (t.slip_count > 0) {
        r.slipped++;
        r.daysLost += slipDays(t);
        if (t.slip_count >= 3) r.pushed3++;
      }
    }
    for (const t of doneTasks.data ?? []) {
      const r = t.assignee ? by.get(t.assignee.id) : undefined;
      if (r) r.closed++;
    }
    for (const r of by.values()) r.clients.sort((a, b) => PRIO_ORDER[a.priority] - PRIO_ORDER[b.priority] || a.name.localeCompare(b.name));
    return [...by.values()].sort((a, b) => b.clients.length - a.clients.length || a.user.name.localeCompare(b.user.name));
  }, [clients.data, users.data, openTasks.data, doneTasks.data, today]);

  // Prototype "Where due dates are slipping": open tasks that moved from their first date, worst first.
  const slipping = useMemo(
    () => (openTasks.data ?? []).filter((t) => t.slip_count > 0).sort((a, b) => slipDays(b) - slipDays(a)),
    [openTasks.data],
  );

  const showRevenue = rows.some((r) => r.revenue !== null);
  const open = (id: string | null) => setParams(id ? { spoc: id } : {});

  if (clients.isPending || (users.isPending && users.fetchStatus !== 'idle')) return <div className="panel"><Loading label="Loading SPOCs…" /></div>;
  if (clients.isError) return <div className="panel"><ErrorState error={clients.error} onRetry={() => clients.refetch()} /></div>;

  const current = who ? rows.find((r) => r.user.id === who) : undefined;
  if (current) return <SpocDetail row={current} showRevenue={showRevenue} onBack={() => open(null)} />;

  const taskNum = (n: number) => (openTasks.isPending && readsTasks ? <span className="sub">…</span> : n);
  return (
    <>
    <div className="panel">
      {rows.length === 0 ? (
        <Empty title="No SPOCs yet">People who look after clients appear here once a client is given a SPOC.</Empty>
      ) : (
        <table>
          <thead>
            <tr>
              <th>SPOC</th>
              <th>Clients</th>
              {showRevenue ? <th className="tr">Revenue handled</th> : null}
              <th className="tr">Open</th>
              <th className="tr"><Lbl>Overdue</Lbl></th>
              <th className="tr">Dates slipped</th>
              <th className="tr">Days lost</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr className="row" key={r.user.id} onClick={() => open(r.user.id)}>
                <td>
                  <div className="split">
                    <Avatar user={r.user} size={26} />
                    <div>
                      <b>{r.user.name}</b>
                      <div className="sub">{doneTasks.isPending && readsTasks ? '…' : `${r.closed} closed`}</div>
                    </div>
                  </div>
                </td>
                <td>
                  {r.clients.map((c) => (
                    <span key={c.id} className={`chip${c.priority === 'high' ? ' brand' : ''}`} style={{ margin: '0 4px 4px 0' }}>
                      {c.name}
                    </span>
                  ))}
                </td>
                {showRevenue ? <td className="tr num">{r.revenue !== null ? rupees(r.revenue) : <span className="sub">—</span>}</td> : null}
                <td className="tr num">{taskNum(r.open)}</td>
                <td className="tr">{r.overdue ? <span className="chip cold">{r.overdue}</span> : <span className="sub">0</span>}</td>
                <td className="tr">{r.slipped ? <span className={`chip ${r.pushed3 ? 'cold' : 'warm'}`}>{r.slipped}</span> : <span className="sub">0</span>}</td>
                <td className="tr num">{taskNum(r.daysLost)}</td>
                <td className="tr">
                  <button className="btn sm">Open</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
    {readsTasks ? (
      <div className="panel" style={{ marginTop: 16 }}>
        <div className="hd">
          <h3>Where due dates are slipping</h3>
          <div className="r">Open tasks that moved from their first date, worst first</div>
        </div>
        {openTasks.isPending ? (
          <Loading />
        ) : openTasks.isError ? (
          <ErrorState error={openTasks.error} onRetry={() => openTasks.refetch()} />
        ) : slipping.length ? (
          slipping.map((t) => {
            const last = t.slips[t.slips.length - 1];
            const d = dueText(t.due, today);
            return (
              <div key={t.id} className="li click" role="button" tabIndex={0} onClick={() => setParams((p) => { const n = new URLSearchParams(p); n.set('task', t.id); return n; })}>
                <div className="b">
                  <div className="t">{t.title}</div>
                  <div className="m">
                    <span>
                      {t.client ? t.client.name : 'Internal'}
                      {t.client?.priority === 'high' ? (
                        <span className="pm" title="High priority client">
                          {' '}▲
                        </span>
                      ) : null}
                    </span>
                    {t.assignee ? <span>{firstName(t.assignee.name)}</span> : null}
                    {t.orig_due ? <span>First due {dayLabel(t.orig_due)}, now {dayLabel(t.due)}</span> : null}
                    {last ? <span>Last reason: {SLIP_REASON_LABEL[last.reason_code]}{last.note ? `, ${last.note}` : ''}</span> : null}
                  </div>
                </div>
                <div style={{ textAlign: 'right', display: 'grid', gap: 5, justifyItems: 'end' }}>
                  <SlipChip task={t} />
                  <span className={`chip ${d.tone}`}>{d.label}</span>
                </div>
              </div>
            );
          })
        ) : (
          <Empty title="No slipped dates">Everything is on its first date.</Empty>
        )}
      </div>
    ) : null}
    </>
  );
}

type Lens = 'client' | 'risk';

function SpocDetail({ row, showRevenue, onBack }: { row: SpocRow; showRevenue: boolean; onBack: () => void }) {
  const { can } = useSession();
  const [lens, setLens] = useState<Lens>('client');
  const [changing, setChanging] = useState<SpocTarget | null>(null);
  const list = lens === 'client' ? row.clients : [...row.clients].sort((a, b) => b.overdue_count + b.open_escalations - (a.overdue_count + a.open_escalations));

  return (
    <>
      <div className="split" style={{ marginBottom: 14 }}>
        <button className="btn sm ghost" onClick={onBack} style={{ marginLeft: -10 }}>
          <Icon name="left" size={14} /> All SPOCs
        </button>
      </div>
      <div className="split" style={{ gap: 14, marginBottom: 16, flexWrap: 'wrap' }}>
        <Avatar user={row.user} />
        <div>
          <h2 style={{ fontSize: 19 }}>{row.user.name}</h2>
          <div className="sub">
            {row.clients.length} client{row.clients.length === 1 ? '' : 's'}
            {showRevenue && row.revenue !== null ? `, ${rupees(row.revenue)} a year` : ''}
          </div>
        </div>
        <div className="grow" />
        <ActAsActions user={{ ...row.user, status: row.status }} />
        {row.overdue ? <span className="chip cold">{row.overdue} overdue</span> : null}
        {row.slipped ? <span className={`chip ${row.pushed3 ? 'cold' : 'warm'}`}>{row.slipped} slipped</span> : null}
      </div>
      <div className="bar">
        <span className="lbl">View</span>
        <Seg options={[['client', 'By priority'], ['risk', 'Most needing attention']]} value={lens} onChange={setLens} />
      </div>
      {list.length === 0 ? (
        <div className="panel">
          <Empty title="No clients yet">Give this person a client from Settings → SPOCs.</Empty>
        </div>
      ) : (
        list.map((c) => (
          <div key={c.id} className={`ccard p-${c.priority} open`} style={{ marginBottom: 12 }}>
            <div className="chd" style={{ cursor: 'default' }}>
              <span className={`dot ${c.band ? bandInfo(c.band).cls : 'mute'}`} title={c.band ? bandInfo(c.band).label : 'No health score yet'} />
              <span className="nm">{c.name}</span>
              <PriorityChip p={c.priority} />
              {c.annual_revenue_lakh != null ? <span className="rv">{rupees(c.annual_revenue_lakh)} / yr</span> : null}
              <div className="grow" />
              {c.overdue_count ? <span className="chip cold">{c.overdue_count} overdue</span> : null}
              {c.open_escalations ? <span className="chip warm">{c.open_escalations} escalation{c.open_escalations === 1 ? '' : 's'}</span> : null}
              {can('client.changeSpoc') ? (
                <button className="btn sm" onClick={() => setChanging({ id: c.id, name: c.name, spoc: c.spoc })}>
                  Change SPOC
                </button>
              ) : null}
            </div>
            <div className="it" style={{ color: 'var(--sub)', cursor: 'default' }}>
              {c.segment} · {c.last_contact_days === null ? 'No contact logged yet' : `Last contact ${c.last_contact_days}d ago`}
            </div>
          </div>
        ))
      )}
      {changing ? <ChangeSpocModal client={changing} onClose={() => setChanging(null)} /> : null}
    </>
  );
}
